import React, { useState } from 'react';
import { X } from 'lucide-react';

const SUBJECTS_OPTIONS = [
  'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English',
  'Hindi', 'History', 'Geography', 'Computer Science', 'Economics',
  'Political Science', 'Accountancy', 'Business Studies', 'Physical Education'
];

export const FacultyForm = ({ initialData, onSubmit, loading, isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    phone: initialData?.phone || '',
    email: initialData?.email || '',
    password: '',
    subjects: initialData?.subjects || [],
    employment_type: initialData?.employment_type || 'full_time',
    joining_date: initialData?.joining_date || new Date().toISOString().split('T')[0],
  });

  const [subjectInput, setSubjectInput] = useState('');
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const addSubject = (subject) => {
    if (subject && !formData.subjects.includes(subject)) {
      setFormData(prev => ({
        ...prev,
        subjects: [...prev.subjects, subject]
      }));
    }
    setSubjectInput('');
  };

  const removeSubject = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter(s => s !== subject)
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\d{10,}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone must have at least 10 digits';
    }
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!isEdit && !formData.password.trim()) {
      newErrors.password = 'Password is required for new faculty accounts';
    } else if (!isEdit && formData.password.length > 0 && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (!formData.joining_date) newErrors.joining_date = 'Joining date is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const filteredSubjects = SUBJECTS_OPTIONS.filter(
    s => s.toLowerCase().includes(subjectInput.toLowerCase()) && !formData.subjects.includes(s)
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="faculty-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Full Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 rounded-md border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors ${
              errors.name ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
            }`}
            placeholder="Enter full name"
            data-testid="input-name"
          />
          {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-3 py-2 rounded-md border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors ${
              errors.phone ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
            }`}
            placeholder="Enter phone number"
            data-testid="input-phone"
          />
          {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 rounded-md border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors ${
              errors.email ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
            }`}
            placeholder="Enter email address"
            data-testid="input-email"
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
        </div>

        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
              Password *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-3 py-2 rounded-md border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors ${
                errors.password ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
              }`}
              placeholder="Set a password for faculty login"
              data-testid="input-password"
            />
            {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Employment Type
          </label>
          <select
            name="employment_type"
            value={formData.employment_type}
            onChange={handleChange}
            className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
            data-testid="input-employment-type"
          >
            <option value="full_time">Full Time</option>
            <option value="part_time">Part Time</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
            Joining Date *
          </label>
          <input
            type="date"
            name="joining_date"
            value={formData.joining_date}
            onChange={handleChange}
            className={`w-full px-3 py-2 rounded-md border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors ${
              errors.joining_date ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'
            }`}
            data-testid="input-joining-date"
          />
          {errors.joining_date && <p className="mt-1 text-sm text-red-500">{errors.joining_date}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
          Subjects
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {formData.subjects.map((subject) => (
            <span
              key={subject}
              className="inline-flex items-center gap-1 bg-primary/10 text-primary dark:bg-accent-cool/20 dark:text-accent-cool px-3 py-1 rounded-full text-sm"
            >
              {subject}
              <button
                type="button"
                onClick={() => removeSubject(subject)}
                className="hover:text-red-500 transition-colors"
                data-testid={`remove-subject-${subject}`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
        <div className="relative">
          <input
            type="text"
            value={subjectInput}
            onChange={(e) => setSubjectInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (subjectInput.trim()) addSubject(subjectInput.trim());
              }
            }}
            className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
            placeholder="Type to search or add a subject..."
            data-testid="input-subjects"
          />
          {subjectInput && filteredSubjects.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-48 overflow-auto">
              {filteredSubjects.map((subject) => (
                <button
                  key={subject}
                  type="button"
                  onClick={() => addSubject(subject)}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm transition-colors"
                >
                  {subject}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
        <button
          type="submit"
          disabled={loading}
          className="bg-primary hover:bg-primary-light text-white shadow-sm rounded-md px-6 py-2.5 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-faculty-form"
        >
          {loading ? 'Saving...' : isEdit ? 'Update Faculty' : 'Create Faculty'}
        </button>
      </div>
    </form>
  );
};
